import React, { useEffect, useRef, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import {
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
} from '@mui/material';
import TextInput from '../Input/TextInput';
import { useTranslation } from 'react-i18next';
import { glb_sv, sendRequest, serviceList } from 'src/utils';
import { toast } from 'react-toastify';
import ButtonAction from '../Button.js/SaveButton';
import ComboBox from '../Input/ComboBox';
import Loader from '../loader/loader';
import { useTheme } from '@emotion/react';
import { LoadingButton } from '@mui/lab';
import Iconify from '../iconify/Iconify';
import { Label } from '@mui/icons-material';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

function BootstrapDialogTitle(props) {
    const { children, onClose, ...other } = props;

    return (
        <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
}

BootstrapDialogTitle.propTypes = {
    children: PropTypes.node,
    onClose: PropTypes.func.isRequired,
};

// Hiển thị thông tin chi tiết của User, thông tin đăng ký trên thiết bị Hikvision và thực hiện đồng bộ dữ liệu đến Hikvision
const InfoUserDetailModal = ({ visible = false, setVisible, userId }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('lg'));

    const [getDetailFlags, setGetDetailFlags] = useState(false);
    const getRefDetail = useRef(false);
    const [infoUser, setInfoUser] = useState([]);
    const [listHikvision, setListHikvision] = useState([]);

    const addFlag = useRef(false);
    const [flagAddStat, setFlagAddStat] = useState(false);

    const TABLE_HEAD = [
        { id: 'ipadd_device', label: t('ip_add_device'), alignRight: false },
        { id: 'portno_device', label: t('portno_device'), alignRight: false },
        { id: 'name_staff', label: t('name_staff'), alignRight: false },
        { id: 'faceID', label: t('FaceID'), alignRight: false },
        { id: 'cardNo', label: t('Card No'), alignRight: false },
        { id: 'status', label: t('status'), alignRight: false },
        { id: 'describe', label: t('describe'), alignRight: false },
    ];

    useEffect(() => {
        if (visible && userId) {
            getByIdUser(userId);
        }
    }, [visible]);

    //Lấy thông tin chi tiết của user
    const getByIdUser = (userId) => {
        if (getRefDetail.current) {
            return;
        }
        getRefDetail.current = true;
        setGetDetailFlags(true);
        const Inval = [userId];
        sendRequest(serviceList.getByIdUser, Inval, getByIdUserResult, true, timeOutFunctGetByIdUser, 40000);
    };

    const getByIdUserResult = (reqInfoMap, message) => {
        getRefDetail.current = false;
        setGetDetailFlags(false);
        if ((message['proc_status']?.toString() || '0') === '0') {
            toast.error('Error: ' + message['proc_message']);
            return;
        } else {
            let data = message.proc_data.rows[0].o_1;
            // console.log("UserDetails", data);
            setInfoUser(data[0][0]);
            setListHikvision(data[1]);
        }
    };

    const timeOutFunctGetByIdUser = (reqInfo) => {
        getRefDetail.current = false;
        setGetDetailFlags(false);
        toast.error(t('error.time_out'));
    };

    // console.log('InfoUserDetailModal', userId, infoUser, listHikvision);

    // Đồng bộ dữ liệu lên Hikvision
    const onClickSyncHikvision = (userId) => {
        if (addFlag.current === true) return;
        addFlag.current = true;
        setFlagAddStat(true);

        const InVal = [[{ user_id_register: userId }]];
        sendRequest(serviceList.syncCardnoFaceIdHik, InVal, actionResult, true, timeOutFunct, 40000);
    };

    const actionResult = (reqInfoMap, message) => {
        addFlag.current = false;
        setFlagAddStat(false);
        if ((message['proc_status']?.toString() || '0') === '0') {
            toast.error('Error: ' + message['proc_message']);
            return;
        } else {
            toast.success(message['proc_message']);
            setTimeout(() => {
                getByIdUser(infoUser?.id);
            }, 1000);
        }
    };
    const timeOutFunct = (reqInfo) => {
        addFlag.current = false;
        setFlagAddStat(false);
        toast.error(t('error.time_out'));
    };

    return (
        <BootstrapDialog
            fullScreen={fullScreen}
            maxWidth={'md'}
            open={visible}
            onClose={() => setVisible(!visible)}
            aria-labelledby="customized-dialog-title"
        >
            <BootstrapDialogTitle id="customized-dialog-title" onClose={() => setVisible(!visible)}>
                {t('info_user')}
            </BootstrapDialogTitle>
            <Loader loading={flagAddStat} />
            <DialogContent dividers>
                <Grid padding={'8px 10px'} container>
                    <Grid container spacing={1} xl={8} md={8} sm={8}>
                        <Grid item style={{ width: '100%' }} xl={12} md={12} sm={12}>
                            <Typography variant="subtitle1" sx={{ ml: 1 }}>
                                {t('code_staff')} : {infoUser?.id}
                            </Typography>{' '}
                        </Grid>
                        <Grid item style={{ width: '100%' }} xl={12} md={12} sm={12}>
                            <Typography variant="subtitle1" sx={{ ml: 1 }}>
                                {t('name_staff')} : {infoUser?.user_nm}
                            </Typography>{' '}
                        </Grid>
                        <Grid item style={{ width: '100%' }} xl={12} md={12} sm={12}>
                            <Typography variant="subtitle1" sx={{ ml: 1 }}>
                                {t('username')} : {infoUser?.accounts}
                            </Typography>{' '}
                        </Grid>
                        <Grid item style={{ width: '100%' }} xl={12} md={12} sm={12}>
                            <Typography variant="subtitle1" sx={{ ml: 1 }}>
                                {t('RFID_code')} : {infoUser?.code_rfid}
                            </Typography>{' '}
                        </Grid>
                        <Grid item style={{ width: '100%' }} xl={12} md={12} sm={12}>
                            <Typography variant="subtitle1" sx={{ ml: 1 }}>
                                {t('Card No')} : {infoUser?.card_no ? infoUser?.card_no : '---'}
                            </Typography>{' '}
                        </Grid>
                        <Grid item style={{ width: '100%' }} xl={12} md={12} sm={12}>
                            <Typography variant="subtitle1" sx={{ ml: 1 }}>
                                {t('Email')} : {infoUser?.email}
                            </Typography>{' '}
                        </Grid>
                        <Grid item style={{ width: '100%' }} xl={12} md={12} sm={12}>
                            <Typography variant="subtitle1" sx={{ ml: 1 }}>
                                {t('phone_supplier')} : {infoUser?.phone}
                            </Typography>{' '}
                        </Grid>
                        <Grid item style={{ width: '100%' }} xl={12} md={12} sm={12}>
                            <Typography variant="subtitle1" sx={{ ml: 1 }}>
                                {t('department')} : {infoUser?.departs_nm}
                            </Typography>{' '}
                        </Grid>
                        <Grid item style={{ width: '100%' }} xl={12} md={12} sm={12}>
                            <Typography variant="subtitle1" sx={{ ml: 1 }}>
                                {t('group_staff')} : {infoUser?.key_mean ? infoUser?.key_mean : '---'}
                            </Typography>{' '}
                        </Grid>
                        <Grid item style={{ width: '100%' }} xl={12} md={12} sm={12}>
                            <Typography variant="subtitle1" sx={{ ml: 1 }}>
                                {t('status')} : {infoUser?.block === '1' ? t('active') : t('band')}
                            </Typography>{' '}
                        </Grid>
                    </Grid>
                    <Grid xl={4} md={4} sm={4}>
                        <Grid item style={{ width: '100%' }} xl={12} md={12} sm={12}>
                            <Typography variant="subtitle1" sx={{ ml: 1 }}>
                                {t('FaceID')} :{' '}
                                {infoUser?.face_img_nm ? (
                                    <img src={`${glb_sv.urlGetFaceImage(infoUser?.face_img_nm)}`} />
                                ) : (
                                    '---'
                                )}
                            </Typography>{' '}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid>
                    <Typography sx={{ ml: 1 }}>{t('list_hikvision')}</Typography>
                </Grid>
                <Grid>
                    <TableContainer sx={{ padding: 1, width: '100%' }}>
                        <Table sx={{ paddingLeft: 2, width: '100%' }}>
                            {/* <UserListHead headLabel={TABLE_HEAD} /> */}
                            <TableHead>
                                <TableRow>
                                    {TABLE_HEAD.map((item, index) => {
                                        return <TableCell key={index}>{item.label}</TableCell>;
                                    })}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {listHikvision.length > 0 &&
                                    listHikvision.map((item, index) => {
                                        return (
                                            <TableRow hover key={index} tabIndex={-1} role="checkbox">
                                                <TableCell align="left" width={200} style={{ minWidth: '150px' }}>
                                                    {item.ipadd_device}
                                                </TableCell>
                                                <TableCell align="left" width={200}>
                                                    {item.portno_device}
                                                </TableCell>
                                                <TableCell align="left" width={200}>
                                                    {item.user_nm ? item.user_nm : '---'}
                                                </TableCell>
                                                <TableCell align="left">
                                                    {item?.face_img_nm ? (
                                                        <img src={`${glb_sv.urlGetFaceImage(item?.face_img_nm)}`} />
                                                    ) : (
                                                        '---'
                                                    )}
                                                </TableCell>
                                                <TableCell align="left" style={{ minWidth: '120px' }}>
                                                    {item.card_no ? item.card_no : '---'}
                                                </TableCell>
                                                <TableCell align="left">
                                                    {item.status === false ? (
                                                        <Iconify icon={'mdi:error-outline'} style={{ color: 'red' }} />
                                                    ) : (
                                                        <Iconify
                                                            icon={'mdi:success-circle-outline'}
                                                            style={{ color: 'green' }}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell align="left" style={{ minWidth: '150px' }}>
                                                    {item.note}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </DialogContent>
            <DialogActions style={{ padding: '12px 16px' }}>
                <ButtonAction onClick={() => setVisible(!visible)} type={'cancel'} title={t('close')} />
                <ButtonAction onClick={() => onClickSyncHikvision(infoUser?.id)} type={'save'} title={t('sync')} />
            </DialogActions>
        </BootstrapDialog>
    );
};

export default InfoUserDetailModal;
